import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { PaymentReceiptTypeViewStore } from "../../store";
import { PaymentReceiptTypeView } from "../view/PaymentReceiptTypeView";
import { PaymentReceiptTypeUpdateStore } from "../../store";
import { PaymentReceiptTypeUpdate } from "../update/PaymentReceiptTypeUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type PaymentReceiptTypeModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const PaymentReceiptTypeModalContent: React.FC<PaymentReceiptTypeModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new PaymentReceiptTypeUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new PaymentReceiptTypeViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <PaymentReceiptTypeView
            paymentReceiptTypeViewStore={
              storeInfo.store as PaymentReceiptTypeViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <PaymentReceiptTypeUpdate
            paymentReceiptTypeUpdateStore={
              storeInfo.store as PaymentReceiptTypeUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
